import _ from 'lodash';
import { PromiseState } from 'react-refetch';
import { createSelector } from 'reselect';
import {
  getItemsListOrdered,
  getListPromiseStateOrdered,
} from '../../../redux/list/list.utils';
import { StoreState } from '../../../redux/reducers/store.types';
import { Flow, FlowVersion } from '../../../types/flow.types';
import { MaybeUndef } from '../../../types/system';
import {
  DetailsPanelState,
  DetailsPanelStateType,
} from '../flow-details-panel/common/parent-child-details.types';
import { isFlowActive } from '../flows.utils';
import { FlowsListFilter, FlowsUiState } from './flows-ui.reducer';
import { FlowsState } from './flows.reducer';

export const getFlows = (state: StoreState): FlowsState => state.flows;

export const getFlowsList = createSelector(
  getFlows,
  (state: FlowsState): Flow[] => getItemsListOrdered(state.flowsList),
);

export const getFlowsUi = createSelector(
  getFlows,
  (state: FlowsState): FlowsUiState => state.flowsUi,
);

export const getFlowsListFiltered = createSelector(
  getFlowsList,
  getFlowsUi,
  (list: Flow[], ui: FlowsUiState): Flow[] => {
    if (ui.flowsListFilter === FlowsListFilter.None) {
      return list;
    }

    const selectActives = ui.flowsListFilter === FlowsListFilter.Active;
    return _.filter<Flow>(
      list,
      (flow: Flow) => isFlowActive(flow) === selectActives,
    );
  },
);

export const getSelectedFlowId = createSelector(
  getFlowsUi,
  (ui: FlowsUiState): MaybeUndef<string> => ui.selectedFlowId,
);

export const getSelectedFlow = createSelector(
  getFlowsListFiltered,
  getSelectedFlowId,
  (visibleFlows: Flow[], flowId: MaybeUndef<string>): MaybeUndef<Flow> => {
    return flowId === undefined
      ? undefined
      : _.find<Flow>(visibleFlows, { id: flowId });
  },
);

export const getSelectedFlowVersionId = createSelector(
  getFlowsUi,
  (ui: FlowsUiState): MaybeUndef<number> => ui.selectedFlowVersionId,
);

export const selectFlowVersionsList = createSelector(
  getFlows,
  (state: FlowsState): PromiseState<FlowVersion[]> =>
    getListPromiseStateOrdered<FlowVersion>(state.flowVersionsList),
);

export const getSelectedFlowVersion = createSelector(
  selectFlowVersionsList,
  getSelectedFlowVersionId,
  (
    versionsList: PromiseState<FlowVersion[]>,
    versionId: MaybeUndef<number>,
  ): MaybeUndef<FlowVersion> => {
    return !versionsList.fulfilled || versionId === undefined
      ? undefined
      : _.find<FlowVersion>(versionsList.value, {
          revision: _.toNumber(versionId),
        });
  },
);

export const getIsFlowVersionDetailsOpened = createSelector(
  getFlowsUi,
  getSelectedFlowVersionId,
  (ui: FlowsUiState, versionId: MaybeUndef<number>): boolean =>
    ui.isVersionDetailsOpened && versionId !== undefined,
);

export const getViewState = createSelector(
  getFlowsUi,
  getSelectedFlow,
  getSelectedFlowVersion,
  (
    ui: FlowsUiState,
    flow: MaybeUndef<Flow>,
    version: MaybeUndef<FlowVersion>,
  ): DetailsPanelState<Flow, FlowVersion> => {
    if (ui.isAddingNew) {
      return { type: DetailsPanelStateType.AddNewParent };
    }

    if (!flow) {
      return { type: DetailsPanelStateType.Empty };
    }

    if (!version || !ui.isVersionDetailsOpened) {
      return { type: DetailsPanelStateType.ParentDetails, parent: flow };
    }

    return {
      type: DetailsPanelStateType.ParentAndChildDetails,
      parent: flow,
      child: version,
    };
  },
);
